import type { ReviewCreditAmounts, ReviewMedia } from './review';

export interface ReviewFormData {
  rating: number;
  title: string;
  content: string;
  nickname: string;
  email: string;
  skinType: string;
  skinConcern: string;
  images: File[];
  recommend: string;
  ageGroup: string;
  gender: string;
  /** Seeded with every existing attachment, so removing one is what shortens the list. */
  keptMedia: ReviewMedia[];
}

/** `edit` reaches the update endpoint, which takes only rating, text and media. */
export type ReviewFormMode = 'create' | 'edit';

/** Keyed by field so each section can show its own; absent means the field passes. */
export interface ValidationErrors {
  rating?: string;
  title?: string;
  content?: string;
  nickname?: string;
  email?: string;
  skinType?: string;
  skinConcern?: string;
  images?: string;
  recommend?: string;
  ageGroup?: string;
  gender?: string;
}

export interface BaseReviewModalProps {
  isOpen: boolean;
  productInfo: {
    name: string;
    brand: string;
    image: string;
    category: string;
  };
  /** What the credit banner asks the server about; optional, since an order line may omit it. */
  productId?: number;
  onClose: () => void;
}

export interface ReviewModalProps extends BaseReviewModalProps {
  onSubmit: (values: ReviewFormData) => Promise<void>;
  mode?: ReviewFormMode;
  initialValues?: Partial<ReviewFormData>;
  existingMedia?: ReviewMedia[];
}

export interface SelectOption {
  value: string;
  label: string;
}

export interface ReviewFormActions {
  setRating: (v: number) => void;
  setTitle: (v: string) => void;
  setContent: (v: string) => void;
  setNickname: (v: string) => void;
  setEmail: (v: string) => void;
  setSkinType: (v: string) => void;
  setSkinConcern: (v: string) => void;
  setImages: (v: File[]) => void;
  setRecommend: (v: string) => void;
  setAgeGroup: (v: string) => void;
  setGender: (v: string) => void;
  setKeptMedia: (v: ReviewMedia[]) => void;
}

export interface ReviewFormOptions {
  skinTypeOptions: SelectOption[];
  skinConcernOptions: SelectOption[];
}

export interface ReviewFormProps {
  formData: ReviewFormData;
  formActions: ReviewFormActions;
  options: ReviewFormOptions;
  productInfo: {
    name: string;
    brand: string;
    image: string;
    category: string;
  };
  productId?: number;
  isSubmitDisabled: boolean;
  /** What blocks submission, so the sections can say it instead of the button going quiet. */
  validationErrors?: ValidationErrors;
  isMember: boolean;
  onSubmit: () => void;
  submissionAlertMessage?: string | null;
  /** Marked inside the body: the message alone leaves the reviewer rereading their paragraph. */
  submissionFlaggedText?: string[];
  mode?: ReviewFormMode;
  isSubmitting?: boolean;
}
